import { z } from 'zod'

/** Reusable scalar schemas. Every API contract is built from these. */

export const UuidSchema = z.uuid()

/** Positive integer member id (members.id, a bigserial). The CSP census index is separate (census_slots, M1). */
export const MemberIdSchema = z.coerce.number().int().positive()
export type MemberId = z.infer<typeof MemberIdSchema>

/** ISO 8601 datetime with offset, e.g. `2026-10-04T17:58:34.012Z`. */
export const IsoDateTimeSchema = z.iso.datetime({ offset: true })
export type IsoDateTime = z.infer<typeof IsoDateTimeSchema>

/** Calendar date `YYYY-MM-DD` (dates of birth, document dates). */
export const IsoDateSchema = z.iso.date()
export type IsoDate = z.infer<typeof IsoDateSchema>

/** ISO 3166-1 alpha-3 country code, upper case (Didit uses alpha-3 everywhere). */
export const CountryIso3Schema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'Expected an ISO 3166-1 alpha-3 country code')
export type CountryIso3 = z.infer<typeof CountryIso3Schema>

export const EmailSchema = z.string().trim().max(254).pipe(z.email())

/** E.164 phone number, e.g. `+34600600600`. */
export const PhoneSchema = z
  .string()
  .trim()
  .regex(/^\+[1-9]\d{6,14}$/, 'Expected an international phone number, e.g. +34600600600')

export const EthAddressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/, 'Expected a 0x-prefixed 20-byte address')
export type EthAddress = z.infer<typeof EthAddressSchema>

/**
 * DAVINCI process id: 0x-prefixed **31 bytes** (62 hex digits), e.g.
 * `0x42fc20654efd78c6887ff0bd1cc50c9ec1dab589f58480020000000000001e` (SDK `validateProcessId`).
 * The API stores and returns them lowercase; compare case-insensitively.
 */
export const ProcessIdSchema = z.string().regex(/^0x[0-9a-fA-F]{62}$/, 'Expected a 0x-prefixed 31-byte process id')
export type ProcessId = z.infer<typeof ProcessIdSchema>

export const HexSchema = z.string().regex(/^0x[0-9a-fA-F]*$/, 'Expected 0x-prefixed hex')

/** 32-byte hex string (e.g. the member auth verifier, a PBKDF2-SHA256 output). */
export const Hex32Schema = z.string().regex(/^0x[0-9a-fA-F]{64}$/, 'Expected 0x-prefixed 32-byte hex')

export const SlugSchema = z
  .string()
  .regex(/^[a-z0-9](?:[a-z0-9-]{1,62}[a-z0-9])$/, 'Expected a lowercase slug (a-z, 0-9, -)')

/** Organizer password policy (organizer passwords reach the server and are hashed with argon2id). */
export const PasswordSchema = z.string().min(8, 'At least 8 characters').max(128)

/** Decimal string for bigint values (results totals, CSP weight/index) so JSON stays lossless. */
export const BigIntStringSchema = z.string().regex(/^\d+$/, 'Expected a non-negative integer string')

export const LatitudeSchema = z.number().min(-90).max(90)
export const LongitudeSchema = z.number().min(-180).max(180)

export const LatLngSchema = z.object({ lat: LatitudeSchema, lng: LongitudeSchema })
export type LatLng = z.infer<typeof LatLngSchema>

export const OkResponseSchema = z.object({ ok: z.literal(true) })
export type OkResponse = z.infer<typeof OkResponseSchema>
